<template>
  <div class="page-table scrollable only-y">
    <resize-observer @notify="__resizeHandler" />
    <div id="stats-advanced-modal">
      <el-dialog
        width="390px"
        :visible="modalVisible"
        :before-close="handleCloseModal"
        :close-on-click-modal="false"
      >
        <h2 class="form-section-header">Filter Settings</h2>

        <h3 class="black-text mb-5">Select Date Range</h3>
        <el-date-picker
          class="mb-10"
          style="margin-left: auto"
          v-model="historyDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="datePickerOptions"
        >
        </el-date-picker>

        <el-form class="form-modal">
          <h3 class="black-text mb-10">Select Distribution</h3>
          <el-radio-group v-model="distribution">
            <el-radio-button type="default" label="daily"
              >Daily</el-radio-button
            >
            <el-radio-button label="monthly">Monthly</el-radio-button>
            <el-radio-button label="hourly">Hourly</el-radio-button>
          </el-radio-group>

          <h3 class="black-text mb-0">Filter By</h3>
          <el-select
            v-model="primaryFilter"
            placeholder="Select Filter"
            clearable
            style="margin-top: 0"
          >
            <template slot="prefix"> Filter By </template>
            <el-option value="branch" label="Branch" />
            <el-option
              v-if="direction === 'IN'"
              value="phoneNumber"
              label="Phone Number (Inbound Only)"
            />
            <el-option
              value="user"
              :label="direction === 'IN' ? 'User (ANSWERED ONLY)' : 'User'"
            />
          </el-select>

          <dads-select
            v-if="primaryFilter === 'user'"
            v-model="extNumber"
            placeholder="Select A User"
            allow-create
            remote
            filterable
            clearable
            :remote-method="searchExtensions"
          >
            <el-option
              v-for="(item, index) in extOptions"
              :key="index"
              :label="item.name + '-' + item.number"
              :value="item.number"
            />
            <template slot="prefix"> Filter By User </template>
          </dads-select>

          <dads-select
            v-if="primaryFilter === 'branch'"
            v-model="branch"
            placeholder="Select A Branch"
            filterable
          >
            <el-option
              v-for="(item, index) in branchOptions"
              :key="index"
              :label="item.name"
              :value="item.id"
            />
            <template slot="prefix"> Filter By Branch </template>
          </dads-select>

          <dads-select
            v-if="primaryFilter === 'phoneNumber'"
            v-model="phoneNumber"
            placeholder="Select A Number"
            filterable
            clearable
            remote
            :remote-method="searchNumbers"
          >
            <el-option
              v-for="(item, index) in numberOptions"
              :key="index"
              :label="numberFormatter(item.number)"
              :value="item.number"
            />
            <template slot="prefix"> Filter By Number </template>
          </dads-select>

          <h3 class="black-text mb-0 mt-10">Select {{ name }} Dispositions</h3>
          <el-checkbox-group v-model="dispositions" class="dispositions-group">
            <div>
              <el-checkbox label="ANSWERED">Answered</el-checkbox>
            </div>
            <div>
              <el-checkbox label="NO ANSWER">No Answer</el-checkbox>
            </div>
            <div>
              <el-checkbox label="BUSY">Busy</el-checkbox>
            </div>
            <div>
              <el-checkbox label="FAILED">Failed</el-checkbox>
            </div>
          </el-checkbox-group>

          <div v-if="direction === 'IN'">
            <div class="flex align-center">
              <h3 class="black-text mb-10">Select Presented Filter</h3>
              <el-popover width="350px" placement="top" trigger="hover">
                <i class="more-info-icon el-icon-question" slot="reference" />
                <p class="text-left black-text" style="max-width: 300px">
                  Presented means a call attempted to reach a user.
                </p>
              </el-popover>
            </div>

            <el-radio-group v-model="presentedFilter">
              <el-radio-button label="Presented">Presented</el-radio-button>
              <el-radio-button label="Not Presented"
                >Not Presented</el-radio-button
              >
              <el-radio-button label="All">All</el-radio-button>
            </el-radio-group>
          </div>

          <h3 class="black-text mb-0">Select A Secondary Metric</h3>
          <el-select
            v-model="secondaryGroup"
            placeholder="Select Secondary Metric"
            clearable
            style="margin-top: 0"
          >
            <template slot="prefix"> Select Secondary Metric </template>
            <el-option
              v-for="(item, index) in secondaryMetrics"
              :key="index"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <div v-if="secondaryOptionsList.length">
            <h3 class="black-text mb-5 mt-5">
              Select {{ getSecondaryGroupName(secondaryGroup) }} Options
            </h3>
            <el-checkbox-group
              v-model="secondaryOptions"
              class="flex column wrap dispositions-group"
            >
              <div v-for="(item, index) in secondaryOptionsList" :key="index">
                <el-checkbox :label="item.value">
                  {{ item.label }}
                </el-checkbox>
              </div>
            </el-checkbox-group>
          </div>

          <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
        </el-form>
      </el-dialog>
    </div>
    <div
      class="table-box page-header card-base card-shadow--medium call-history-plot-view"
    >
      <div class="flex">
        <h2 class="mt-0">
          <v-svg
            v-if="direction === 'IN'"
            sprite="call-inbound"
            class="voxo-pink"
          ></v-svg>
          <v-svg v-else sprite="call-outbound" class="voxo-pink"></v-svg>
          {{ name }}
        </h2>
        <el-radio-group
          v-model="presetSelection"
          @change="doPreset"
          class="ml-10"
        >
          <el-radio-button type="default" label="last10"
            >Last 10 Days</el-radio-button
          >
          <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
        </el-radio-group>
        <el-tooltip placement="top" :content="selectedDateRange">
          <template slot="content">
            {{ selectedDateRange }}
            <br />
            Presentation: {{ presentedFilter }}
          </template>
          <el-button
            @click="modalVisible = true"
            style="margin-left: auto; height: 40px"
          >
            Filter
          </el-button>
        </el-tooltip>
        <el-button
          @click="resetPlot"
          v-if="advancedActive"
          style="background-color: #f25a6b; color: #fff; height: 40px"
        >
          Reset
        </el-button>
        <el-button class="mb-10" @click="doExport" style="height: 40px">
          Export
        </el-button>
      </div>
      <div
        :id="'activity-plot-' + direction"
        style="height: 385px; width: 100%"
        class="mt-10"
        v-loading="plotLoading"
      ></div>
    </div>

    <div
      class="table-box page-header card-base card-shadow--medium call-history-plot-view mt-20"
    >
      <heatmap-plot
        ref="heatMap"
        v-observe-visibility="heatmapVisibility"
        :direction="direction"
        :metaData="{
          service: 'call-history',
          queryType: 'callStatistics',
          tenantId: selectedTenant.id,
          extNumber: extNumber,
          dispositions: dispositions,
        }"
      />
    </div>
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import echarts from "echarts";
import moment from "moment";
import DadsSelect from "@/components/select";
import { ExportToCsv } from "export-to-csv";
import HeatmapPlot from "@/components/heatmap-plot.vue";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "ActivityPlot",
  props: {
    direction: { required: true },
    name: { default: null },
    maxTicks: { default: 9 },
    branchOptions: { required: true },
    colors: {
      default: () => ["#33aaee", "#35be8b", "#f25a6b", "#c485db"],
    },
    colorsSecondary: {
      default: () => ["#f79ca6", "#4cc3d2", "#ff8f02", "#0F85FF"],
    },
  },
  components: { DadsSelect, HeatmapPlot },
  data() {
    return {
      presentedFilter: "All",
      plotLoading: false,
      primaryData: null,
      secondaryData: null,
      chartElement: null,
      chart: null,
      secondaryGroup: null,
      extOptions: [],
      numberOptions: [],
      extNumber: "",
      branch: "",
      phoneNumber: "",
      secondaryOptions: [],
      secondaryOptionsList: [],
      primaryFilter: "",
      dispositions: ["ANSWERED", "NO ANSWER", "BUSY", "FAILED"],
      primaryLegend: [],
      xInterval: 0,
      presetSelection: "last10",
      startDate: null,
      endDate: null,
      modalVisible: false,
      advancedActive: false,
      resized: false,
      distribution: "daily",
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    colorTheme() {
      return this.$store.state.layout.theme;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.endDate && this.startDate) {
        const dateStart = moment(this.startDate).format("M/D/YY");
        const dateEnd = moment(this.endDate).format("M/D/YY");
        return `${dateStart} - ${dateEnd}`;
      }
    },
    secondaryMetrics() {
      if (this.direction === "IN") {
        return [
          {
            value: "callQuality",
            label: "Call Quality",
          },
          {
            value: "internalCalls",
            label: "Internal Calls",
          },
          {
            value: "outboundCalls",
            label: "Outbound Calls",
          },
          {
            value: "talkTime",
            label: "Talk Time",
          },
          {
            value: "totalCalls",
            label: "Total Calls",
          },
        ];
      }
      if (this.direction === "OUT") {
        return [
          {
            value: "callQuality",
            label: "Call Quality",
          },
          {
            value: "inboundCalls",
            label: "Inbound Calls",
          },
          {
            value: "internalCalls",
            label: "Internal Calls",
          },
          {
            value: "talkTime",
            label: "Talk Time",
          },
          {
            value: "totalCalls",
            label: "Total Calls",
          },
        ];
      }
      return [];
    },
  },
  watch: {
    secondaryGroup() {
      const dispositionGroups = ["inboundCalls", "outboundCalls", "totalCalls"];
      if (dispositionGroups.includes(this.secondaryGroup)) {
        this.secondaryOptionsList = [
          {
            value: "ANSWERED",
            label: "Answered",
          },
          {
            value: "NO ANSWER",
            label: "No Answer",
          },
          {
            value: "BUSY",
            label: "Busy",
          },
          {
            value: "FAILED",
            label: "Failed",
          },
        ];
        this.secondaryOptions = this.secondaryOptionsList.map(
          (item) => item.value
        );
        return;
      }
      if (this.secondaryGroup === "callQuality") {
        this.secondaryOptionsList = [];
        this.secondaryOptions = [];
        return;
      }
      if (this.secondaryGroup === "talkTime") {
        this.secondaryOptionsList = [
          {
            value: "inbound",
            label: "Inbound",
          },
          {
            value: "outbound",
            label: "Outbound",
          },
          {
            value: "total",
            label: "Total",
          },
        ];
        this.secondaryOptions = this.secondaryOptionsList.map(
          (item) => item.value
        );
        return;
      }
      this.secondaryOptionsList = [];
      this.secondaryOptions = [];
    },
    selectedTenant() {
      this.resetPlot();
    },
  },
  methods: {
    getSecondaryGroupName(val) {
      const map = {
        callQuality: "Call Quality",
        totalCalls: "Total Calls",
        internalCalls: "Internal Calls",
        inboundCalls: "Inbound Calls",
        outboundCalls: "Outbound Calls",
        talkTime: "Talk Time",
      };
      return map[val];
    },
    searchExtensions(query) {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");
      feathersClient
        .service("extensions")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $or: [
              { number: { $like: `%${searchTerm}%` } },
              { name: { $like: `%${searchTerm}%` } },
            ],
            $limit: 25,
          },
        })
        .then((res) => {
          this.extOptions = res;
        });
    },
    searchNumbers(query) {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");
      feathersClient
        .service("dids")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            number: { $like: `%${searchTerm}%` },
            $limit: 25,
          },
        })
        .then((res) => {
          this.numberOptions = res;
        });
    },
    handleCloseModal() {
      this.modalVisible = false;
    },
    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },
    resetPlot() {
      this.advancedActive = false;
      this.dispositions = ["ANSWERED", "NO ANSWER", "BUSY", "FAILED"];
      this.primaryFilter = "";
      this.secondaryGroup = "";
      this.extNumber = "";
      this.branch = "";
      this.phoneNumber = "";
      this.extOptions = [];
      this.numberOptions = [];
      this.secondaryOptions = [];
      this.secondaryOptionsList = [];
      this.advancedActive = false;
      this.presentedFilter = "All";
      this.distribution = "daily";
      this.doPreset("last10");
    },
    doPreset(val) {
      if (val === "last10") {
        this.endDate = moment().format("YYYY-MM-DD");
        this.startDate = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryData();
      }

      if (val === "lastMonth") {
        this.endDate = moment().format("YYYY-MM-DD");
        this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryData();
      }
    },
    doAdvanced() {
      this.advancedActive = true;
      this.startDate = moment(this.historyDateRange[0]).format("YYYY-MM-DD");
      this.endDate = moment(this.historyDateRange[1]).format("YYYY-MM-DD");
      this.updateDateRangePicker();
      this.queryData();
      this.handleCloseModal();
      this.presetSelection = null;
    },
    getDatesInRange() {
      if (this.distribution === "daily") {
        const dates = [moment(this.startDate).format("MM/DD")];
        let currentDate = this.startDate;
        let endDate = moment(this.endDate).format("YYYY-MM-DD");
        while (currentDate < endDate) {
          const nextDay = moment(currentDate).add(1, "day");
          currentDate = nextDay.format("YYYY-MM-DD");
          dates.push(nextDay.format("MM/DD"));
        }
        return dates;
      }

      if (this.distribution === "monthly") {
        const dates = [moment(this.startDate).format("MMMM")];
        let currentDate = this.startDate;
        let endDate = moment(this.endDate).format("YYYY-MM");
        while (currentDate < endDate) {
          const nextMonth = moment(currentDate).add(1, "month");
          currentDate = nextMonth.format("YYYY-MM");
          dates.push(nextMonth.format("MMMM"));
        }
        return dates;
      }

      if (this.distribution === "hourly") {
        const dates = [moment(this.startDate).format("YYYY-MM-DD_hA")];
        let currentDate = this.startDate;
        while (currentDate < this.endDate) {
          const nextHour = moment(currentDate).add(1, "hour");
          currentDate = nextHour.format("YYYY-MM-DD HH:mm:ss");
          if (currentDate < this.endDate) {
            dates.push(nextHour.format("YYYY-MM-DD_hA"));
          }
        }
        return dates;
      }

      return [];
    },
    buildX() {
      let dateKeys = this.getDatesInRange();
      this.xInterval = 0;

      if (dateKeys.length > this.maxTicks) {
        this.xInterval = Math.ceil(dateKeys.length / this.maxTicks);
      }

      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        data: dateKeys,
        boundaryGap: dateKeys.length <= 10,
        axisLabel: {
          interval: this.xInterval,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: axisColor,
          },
        },
        axisTick: {
          interval: this.xInterval,
        },
      };
    },
    buildY1() {
      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        name: "Calls",
        position: "left",
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
      };
    },
    buildY2() {
      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      //this will be time or calls or percent based on secondary group.
      const unitsKey = {
        internalCalls: "Calls (INTERNAL)",
        outboundCalls: "Calls (OUT)",
        inboundCalls: "Calls (IN)",
        callQuality: "Percent",
        talkTime: "Duration (Min)",
        totalCalls: "Calls (TOTAL)",
      };

      const axisName = unitsKey[this.secondaryGroup];

      return {
        name: axisName,
        position: "right",
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
      };
    },
    buildPrimary(xAxis) {
      this.primaryLegend = [];

      if (this.primaryData) {
        const callData = { ...this.primaryData.data };

        if (this.primaryFilter === "user" && this.direction === "IN") {
          this.dispositions = ["ANSWERED"];
        }

        let dataGroups = {};
        for (let i = 0; i < this.dispositions.length; i++) {
          dataGroups[this.dispositions[i]] = [];
        }

        for (let i = 0; i < xAxis.length; i++) {
          for (let k = 0; k < this.dispositions.length; k++) {
            dataGroups[this.dispositions[k]][i] = 0;
          }

          if (callData.hasOwnProperty(xAxis[i])) {
            for (let k = 0; k < callData[xAxis[i]].length; k++) {
              const callInfo = callData[xAxis[i]][k];
              dataGroups[callInfo.disposition][i] = callInfo.total;
            }
          }
        }

        let series = [];
        let count = 0;
        for (let group in dataGroups) {
          if (dataGroups.hasOwnProperty(group)) {
            series.push({
              name: `${group} (${this.direction})`,
              type: xAxis.length > 13 ? "line" : "bar",
              symbol: "circle",
              smooth: true,
              showSymbol: true,
              yAxisIndex: 0,
              symbolRepeat: true,
              symbolSize: 3,
              symbolMargin: 1,
              barMaxWidth: 5,
              z: -10,
              lineStyle: {
                color: this.colors[count],
              },
              itemStyle: {
                color: this.colors[count],
                borderColor: this.colors[count],
                borderWidth: 3,
              },
              data: dataGroups[group],
            });
            this.primaryLegend.push({ name: `${group} (${this.direction})` });
            count++;
          }
        }
        return series;
      }

      return [];
    },
    buildSecondary(xAxis) {
      const callData = { ...this.secondaryData.data };

      const dispositionGroups = ["outboundCalls", "inboundCalls", "totalCalls"];

      //need to initialize the data groups here.

      let dataGroups = {};

      //this is the initializing for call type secondaries
      if (dispositionGroups.includes(this.secondaryGroup)) {
        if (
          this.secondaryGroup === "inboundCalls" &&
          this.primaryFilter === "user"
        ) {
          this.secondaryOptions = ["ANSWERED"];
        }

        for (let i = 0; i < this.secondaryOptions.length; i++) {
          dataGroups[this.secondaryOptions[i]] = [];
        }
      }

      //this is the initialization for call quality
      if (this.secondaryGroup === "callQuality") {
        dataGroups["AVG PacketLoss"] = [];
        dataGroups["AVG Jitter"] = [];
      }

      //this is the initialization for talk time trash
      if (this.secondaryGroup === "talkTime") {
        for (let i = 0; i < this.secondaryOptions.length; i++) {
          dataGroups[this.secondaryOptions[i]] = [];
        }
      }

      //this is the initializations for internal calls
      if (this.secondaryGroup === "internalCalls") {
        dataGroups["Internal"] = [];
      }

      for (let i = 0; i < xAxis.length; i++) {
        for (let k = 0; k < this.secondaryOptions.length; k++) {
          dataGroups[this.secondaryOptions[k]][i] = 0;
        }

        //if this is outboundcalls, inboundcalls this is fine
        if (
          dispositionGroups.includes(this.secondaryGroup) &&
          callData.hasOwnProperty(xAxis[i])
        ) {
          for (let k = 0; k < callData[xAxis[i]].length; k++) {
            const callInfo = callData[xAxis[i]][k];
            dataGroups[callInfo.disposition][i] = callInfo.total;
          }
        }

        //if this is the internal calls group
        if (this.secondaryGroup === "internalCalls") {
          dataGroups["Internal"][i] = 0;
          if (callData.hasOwnProperty(xAxis[i])) {
            dataGroups["Internal"][i] = callData[xAxis[i]][0].total;
          }
        }

        //if this is the call quality group
        if (this.secondaryGroup === "callQuality") {
          dataGroups["AVG PacketLoss"][i] = 0;
          dataGroups["AVG Jitter"][i] = 0;
          if (callData.hasOwnProperty(xAxis[i])) {
            dataGroups["AVG PacketLoss"][i] =
              callData[xAxis[i]][0].avgPacketLoss;
            dataGroups["AVG Jitter"][i] =
              callData[xAxis[i]][0].avgPercentJitter;
          }
        }

        //if this is the talk time options
        if (
          this.secondaryGroup === "talkTime" &&
          callData.hasOwnProperty(xAxis[i])
        ) {
          const directionMap = {
            IN: "inbound",
            OUT: "outbound",
          };

          //build out total stuff if the options included
          if (this.secondaryOptions.includes("total")) {
            //add up the durations of all objects for each date
            for (let k = 0; k < callData[xAxis[i]].length; k++) {
              const callInfo = callData[xAxis[i]][k];
              dataGroups["total"][i] += +callInfo.totalDuration;
            }
          }

          for (let k = 0; k < callData[xAxis[i]].length; k++) {
            //map durations for each date based on direction
            const callInfo = callData[xAxis[i]][k];
            if (
              this.secondaryOptions.includes(directionMap[callInfo.direction])
            ) {
              dataGroups[directionMap[callInfo.direction]][i] =
                +callInfo.totalDuration;
            }
          }
        }
      }

      //loop over the data groups and build out the series
      let series = [];
      let count = 0;
      for (let group in dataGroups) {
        if (dataGroups.hasOwnProperty(group)) {
          series.push({
            name: `${group} (${this.getSecondaryGroupName(
              this.secondaryGroup
            )})`,
            type: xAxis.length === 1 ? "bar" : "line",
            symbol: "circle",
            smooth: true,
            showSymbol: true,
            yAxisIndex: 1,
            symbolRepeat: true,
            symbolSize: 3,
            symbolMargin: 1,
            barMaxWidth: 5,
            z: -10,
            lineStyle: {
              color: this.colorsSecondary[count],
            },
            itemStyle: {
              color: this.colorsSecondary[count],
              borderColor: this.colorsSecondary[count],
              borderWidth: 3,
            },
            data: dataGroups[group],
          });
          this.primaryLegend.push({
            name: `${group} (${this.getSecondaryGroupName(
              this.secondaryGroup
            )})`,
          });
          count++;
        }
      }
      return series;
    },
    buildChart() {
      if (this.chart) {
        this.chart.dispose();
      }

      this.chart = echarts.init(this.chartElement);

      const xAxis = this.buildX();
      const y1 = this.buildY1();
      const data1 = this.buildPrimary(xAxis.data);

      let y2 = null;
      let data2 = null;
      if (this.secondaryData) {
        if (this.secondaryData.data) {
          y2 = this.buildY2();
          data2 = this.buildSecondary(xAxis.data);
        }
      }

      const seriesData = data2 ? [...data1, ...data2] : data1;
      const yAxis = y2 ? [y1, y2] : [y1];

      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      this.chart.setOption({
        grid: {},
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },
        xAxis,
        yAxis,
        series: seriesData,
        legend: {
          show: true,
          type: "scroll",
          orient: "horizontal",
          icon: "rectangle",
          itemWidth: 15,
          itemHeight: 3,
          data: this.primaryLegend,
          textStyle: {
            color: axisColor,
          },
        },
      });
    },
    queryData() {
      //grab all the data
      this.plotLoading = true;
      feathersClient
        .service("call-history")
        .find({
          query: {
            queryType: "callStatistics",
            tenantId: this.selectedTenant.id,
            dateStart: this.startDate,
            dateEnd: this.endDate,
            direction: this.direction,
            dispositions: this.dispositions,
            primaryName: this.name,
            primaryFilter: this.primaryFilter,
            extNumber: this.extNumber,
            phoneNumber: this.phoneNumber.replace(
              /['":;/{}()[|\-.,<>?_=+ \]]/g,
              ""
            ),
            branch: this.branch,
            secondaryGroup: this.secondaryGroup,
            secondaryOptions: this.secondaryOptions,
            presentedFilter: this.presentedFilter,
            timezone: this.user.timezone,
            distribution: this.distribution,
          },
        })
        .then((res) => {
          if (res === "Hourly Exceed") {
            this.$notify({
              type: "error",
              title: "Time Range",
              message: "Hourly distribution is limited to 2 days",
            });
            this.plotLoading = false;
            return;
          }

          this.primaryData = { ...res.primary };
          this.secondaryData = { ...res.secondary };
          this.buildChart();
          this.plotLoading = false;
        });
    },
    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },
    doExport() {
      const primaryFilterMap = {
        branch: this.branch
          ? this.branchOptions.find((item) => item.id === this.branch).name
          : "",
        phoneNumber: this.phoneNumber.replace(
          /['":;/{}()[|\-.,<>?_=+ \]]/g,
          ""
        ),
        user: this.extNumber,
      };

      const secondaryFieldMap = {
        callQuality: ["avgPacketLoss", "avgPercentJitter"],
      };

      const callTotals = [
        "inboundCalls",
        "outboundCalls",
        "internalCalls",
        "totalCalls",
      ];
      const timeTotals = ["talkTime"];

      //top level keys are dates
      //object level keys are column headers
      let csvData = [];

      //work on the primary data first
      if (this.primaryData && this.primaryData.name && this.primaryData.data) {
        csvData.push({ name: "Inbound Activity Plot Export", total: "" });

        if (this.primaryFilter) {
          csvData.push({
            name: this.primaryFilter,
            total: primaryFilterMap[this.primaryFilter],
          });
        }

        csvData.push({ name: "Primary Data", total: "" });

        for (let key in this.primaryData.data) {
          const currentSet = this.primaryData.data[key];

          csvData.push({ name: key, total: "" });

          for (let item of currentSet) {
            csvData.push({ name: item.disposition, total: item.total });
          }
        }
      }

      if (
        this.secondaryData &&
        this.secondaryData.name &&
        this.secondaryData.data
      ) {
        csvData.push({ name: "Secondary Data", total: "" });
        csvData.push({ name: this.secondaryData.name, total: "" });

        for (let key in this.secondaryData.data) {
          const currentSet = this.secondaryData.data[key];
          csvData.push({ name: key, total: "" });

          for (let item of currentSet) {
            if (callTotals.includes(this.secondaryData.name)) {
              csvData.push({ name: item.disposition, total: item.total });
              continue;
            }

            if (timeTotals.includes(this.secondaryData.name)) {
              csvData.push({
                name: item.direction,
                total: `${item.totalDuration} min`,
              });
              continue;
            }

            for (let metric of secondaryFieldMap[this.secondaryData.name]) {
              csvData.push({ name: metric, total: item[metric] });
            }
          }
        }
      }

      const options = {
        filename: `${this.direction}_activity_plot`,
        showLabels: false,
        headers: ["", ""],
      };

      if (csvData.length) {
        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(csvData);
        return;
      }

      this.$notify({
        type: "warning",
        title: "No Data",
        message: "The data set is empty",
      });
    },

    __resizeHandler() {
      // eslint-disable-next-line no-undef
      const resize = _.throttle(() => {
        setTimeout(() => {
          if (this.chart) {
            this.chart.resize();
          }
          const { chart } = this.$refs["heatMap"];
          if (chart) {
            this.$refs.heatMap.chart.resize();
          }
        }, 1000);
      }, 700);
      resize();
    },

    heatmapVisibility(isVisible) {
      if (isVisible) {
        setTimeout(() => {
          this.$refs.heatMap.buildCalendarHeatmap();
        }, 500);
      }
    },
  },
  mounted() {
    this.chartElement = document.getElementById(
      "activity-plot-" + this.direction
    );
    this.doPreset("last10");
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.el-form {
  .el-select {
    margin-left: 0;
    margin-top: 0;
    margin-bottom: 0;
  }
}

.dispositions-group {
  .el-checkbox {
    margin-left: 0;
    display: inline-block;
    margin-top: 5px;
  }
}

.more-info-icon {
  font-size: 18px;
  color: $voxo-blue;
  margin-top: 8px;
  margin-left: 5px;
  align-self: flex-end;
  margin-bottom: 5px;
}
</style>
